/**
 * Inteligencia artificial — todo lo de IA en un solo lugar (contrato v5).
 *
 * Antes esto estaba partido en dos entradas de menú: "Configuración IA" y
 * "Salud IA". Ahora la salud del pipeline es el primer tab de este panel, que
 * es donde uno la va a buscar. El tab de Almacenamiento se fue a su propia
 * vista (/admin/almacenamiento): el object store no es IA.
 *
 * SOLO front. La configuración se sirve por un único BFF (calidad-api) con el
 * JWT de beartrack — ver src/services/calidadIaConfigService.js. No toca la
 * capa de datos de beartrack. Los workers leen los cambios en vivo (~15 s).
 *
 * Sub-navegación:
 *   • Salud    → monitor del pipeline de análisis de imágenes (AiPerformanceTab).
 *   • General  → monitoreo read-only del servidor Ollama (modelo/VRAM, workers, colas).
 *   • Helpdesk → system prompt del chat + perfiles free/portal + config ollama.
 *   • Calidad  → params de generación (Ollama) + umbrales geo + webhook + 2 prompts.
 *
 * Pre-llenado: los campos se hidratan desde la estructura ANIDADA del GET; los
 * PUT van parciales por sección (Calidad) o por recurso (Helpdesk).
 */
import { useState, useEffect, useCallback } from "react";
import {
  Bot,
  Cpu,
  HeartPulse,
  Server,
  Headset,
  ShieldCheck,
  SlidersHorizontal,
  MapPin,
  Webhook as WebhookIcon,
  FileText,
  Save,
  RotateCcw,
  RefreshCw,
  AlertTriangle,
  Eye,
  EyeOff,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import useUrlState from "../../../hooks/useUrlState";
import AiPerformanceTab from "../deliveryQuality/tabs/AiPerformanceTab";
// AiPerformanceTab es Bootstrap (Card/table/recharts) y depende de estos
// estilos scopeados a `.delivery-quality-view` para respetar light/dark.
import "../deliveryQuality/DeliveryQuality.scss";
import { calidadIaConfigService } from "../../../services/calidadIaConfigService";
import { showToast } from "../../../utils/showToast";
import {
  apiBodyMessages,
  apiErrorMessage,
  apiErrorStatus,
} from "../../../utils/apiErrorMessage";
import { PageHeader } from "../../../components/ui/PageHeader";
import { SectionCard } from "../../../components/ui/SectionCard";
import { Input } from "../../../components/ui/Input";
import { Button } from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/Badge";
import { Switch } from "../../../components/ui/Switch";
import { KV } from "../../../components/ui/KV";
import { Skeleton } from "../../../components/ui/Skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/Tabs";

const MAX_PROMPT = 200000;

// ── Specs de campos por sección ──────────────────────────────────────────────
// `num` → input numérico (validado contra bounds si existen); `bool` → Switch;
// `secret` → input password con toggle; `mono` → nombre técnico en monospace.

const OLLAMA_FIELDS = [
  { key: "temperature", label: "Temperatura", num: true, float: true, mono: true },
  { key: "top_p", label: "Top-P (núcleo)", num: true, float: true, mono: true },
  { key: "num_ctx", label: "Ventana de contexto", num: true, mono: true },
  { key: "num_predict", label: "Máx. de tokens a generar", num: true, mono: true },
  { key: "repeat_penalty", label: "Penalización por repetición", num: true, float: true, mono: true },
  { key: "max_attempts", label: "Reintentos máximos", num: true, mono: true },
  { key: "keep_alive", label: "Keep-alive", mono: true, placeholder: "30m", hint: "Cuánto mantiene Ollama el modelo en memoria (p. ej. 30m, 1h)." },
];
const GEO_FIELDS = [
  { key: "valido_max_m", label: "Válido — distancia máx. (m)", num: true },
  { key: "dudoso_max_m", label: "Dudoso — distancia máx. (m)", num: true },
];
const WEBHOOK_FIELDS = [
  { key: "url", label: "URL de entrega", placeholder: "https://…", full: true },
  { key: "secret", label: "Secreto de firma", secret: true, full: true },
];
// Perfiles del chat (free/portal). Sin bounds/defaults en el contrato.
const HELPDESK_MODEL_FIELDS = [
  { key: "temperature", label: "Temperatura", num: true, float: true, mono: true },
  { key: "top_p", label: "Top-P (núcleo)", num: true, float: true, mono: true },
  { key: "top_k", label: "Top-K", num: true, mono: true },
  { key: "num_ctx", label: "Ventana de contexto", num: true, mono: true },
  { key: "repeat_penalty", label: "Penalización por repetición", num: true, float: true, mono: true },
  { key: "num_predict", label: "Máx. de tokens a generar", num: true, mono: true, hint: "-1 = sin límite." },
  { key: "think", label: "Modo “think”", bool: true, mono: true, hint: "Con qwen debe quedar desactivado (rompe la generación)." },
];
const HELPDESK_OLLAMA_FIELDS = [
  { key: "keep_alive", label: "Keep-alive", mono: true, placeholder: "30m", hint: "Compartido con Calidad. Cuánto mantiene Ollama el modelo en memoria." },
  { key: "preload_on_startup", label: "Precargar al iniciar", mono: true, placeholder: "1", hint: "1 = precargar el modelo al arrancar; 0 = no." },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const strOrEmpty = (v) => (v == null ? "" : String(v));
const fieldVal = (f, v) => (f.bool ? !!v : strOrEmpty(v));

const formFrom = (fields, values) =>
  Object.fromEntries(fields.map((f) => [f.key, fieldVal(f, values?.[f.key])]));

const buildPatch = (fields, form) =>
  Object.fromEntries(
    fields.map((f) => [f.key, f.bool ? !!form[f.key] : f.num ? Number(form[f.key]) : form[f.key]]),
  );

function fmtDate(v, t) {
  if (!v) return t("Nunca");
  const d = dayjs(v);
  return d.isValid() ? d.format("DD-MM-YYYY HH:mm") : String(v);
}

function fmtGB(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return "—";
  return `${(Number(bytes) / 1024 ** 3).toFixed(1)} GB`;
}


// Validación local contra bounds: [min, max]. Espeja el 422 del backend.
function numError(raw, bounds, t) {
  if (raw === "" || raw == null) return t("Requerido");
  const n = Number(raw);
  if (!Number.isFinite(n)) return t("Número inválido");
  if (Array.isArray(bounds)) {
    const [min, max] = bounds;
    if ((min != null && n < min) || (max != null && n > max)) {
      return t("Fuera de rango ({{min}}–{{max}})", { min, max });
    }
  }
  return null;
}

// El back exige valido_max_m < dudoso_max_m (si no, 400). Se valida también en UI.
function geoCrossError(form, t) {
  const v = Number(form.valido_max_m);
  const d = Number(form.dudoso_max_m);
  if (Number.isFinite(v) && Number.isFinite(d) && v >= d) {
    return t("El umbral “válido” debe ser menor que el “dudoso”.");
  }
  return null;
}

// 401 detail calidad-ia emits when the Authorization header is not a bearer token.
const SESSION_EXPIRED_DETAIL = "falta bearer token";

// A 401 whose body says the token is missing/expired/unauthorized means "log in
// again", not "the AI API refused your role" — that one is the 403 path. Once the
// beartrack JWT expires the front drops the header entirely, and calidad-ia
// answers "no autorizado: se requiere api-key o jwt admin".
const isSessionExpired = (detail, status) =>
  status === 401 &&
  typeof detail === "string" &&
  (detail === SESSION_EXPIRED_DETAIL ||
    /expired/i.test(detail) ||
    detail.startsWith("no autorizado"));

// calidad-ia relays ollama-ui failures as `e.response.text[:200]`, so `detail`
// can arrive already serialized: {"detail":"{\"detail\":\"content is required\"}"}.
// A leading '{' is the only marker available for that shape.
const isSerializedDetail = (message) =>
  typeof message === "string" && message.trim().startsWith("{");

// Inner text of a relayed body. Returns null when the blob is truncated (the
// 200-char cut can leave invalid JSON), is not an object, or carries no
// message: the caller then falls back on the status, never on the raw JSON.
const unwrapSerializedDetail = (message, status) => {
  if (!isSerializedDetail(message)) return null;
  let parsed;
  try {
    parsed = JSON.parse(message.trim());
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const nested = apiErrorMessage({ response: { status, data: parsed } });
  if (nested) return nested;
  // apiErrorMessage drops bare status phrases ('Unauthorized') as noise, but a
  // relayed detail is still more specific than the generic status fallback —
  // except for an HTML page, which is never shown.
  const raw = apiBodyMessages(parsed).join(" · ");
  return raw && !raw.startsWith("<") ? raw : null;
};

// API error message. The backend text (FastAPI `detail`) wins when it carries
// anything usable; the status only picks the fallback or the session notice.
export function apiErrMsg(err, t, onLoad) {
  const status = apiErrorStatus(err);
  const backendMessage = apiErrorMessage(err);
  // Session check on the first level only: a relayed ollama-ui 401
  // ('{"detail":"Unauthorized"}', no X-Admin-Secret) is not an expired
  // beartrack JWT and must not read as "your session expired".
  if (isSessionExpired(backendMessage, status))
    return t("Tu sesión expiró. Vuelve a iniciar sesión.");
  const unwrapped = unwrapSerializedDetail(backendMessage, status);
  if (unwrapped) return unwrapped;
  if (backendMessage && !isSerializedDetail(backendMessage)) return backendMessage;
  if (status === 401)
    return t(
      "La API de calidad-ia no validó el token (401). El servidor de IA puede no tener aún activada la validación JWT.",
    );
  if (status === 403)
    return t(
      "La API de calidad-ia aceptó el token pero lo rechazó por permisos (403): requiere un rol admin en el JWT.",
    );
  if (status === 400) return t("Datos inválidos: revisa los valores.");
  if (status === 422) return t("Algún valor está fuera de rango.");
  return onLoad
    ? t("No se pudo cargar la configuración. Reintenta.")
    : t("No se pudo guardar. Reintenta.");
}

function sourceLabel(source, t) {
  if (source === "db") return t("Base de datos");
  if (source === "default") return t("Por defecto");
  return t("Archivo");
}

// ── Presentación ─────────────────────────────────────────────────────────────

function Field({ label, hint, error, className = "", children }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label className="text-sm font-medium text-fg">{label}</label>
      {children}
      {error ? (
        <span className="text-xs text-status-error">{error}</span>
      ) : hint ? (
        <span className="text-xs text-fg-muted">{hint}</span>
      ) : null}
    </div>
  );
}

function ParamLabel({ children, name }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {children}
      <code className="font-mono text-[0.6875rem] text-fg-subtle bg-bg-subtle rounded px-1 py-0.5">
        {name}
      </code>
    </span>
  );
}

function UpdatedAt({ value, t }) {
  return (
    <span className="text-xs text-fg-muted whitespace-nowrap">
      {t("Actualizado")}: {fmtDate(value, t)}
    </span>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-bg p-3">
      <div className="text-xs text-fg-muted">{label}</div>
      <div className="text-xl font-semibold text-fg tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function SecretInput({ value, onChange, disabled, placeholder }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        className="pr-10 font-mono"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        tabIndex={-1}
        aria-label={show ? t("Ocultar") : t("Mostrar")}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-subtle hover:text-fg transition-colors"
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function InlineError({ message, onRetry, t }) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-lg border border-border bg-status-error-soft p-4">
      <div className="flex items-start gap-2 text-sm text-status-error">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
        <span>{message}</span>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry} iconLeft={<RefreshCw size={14} />}>
        {t("Reintentar")}
      </Button>
    </div>
  );
}

function SectionsSkeleton() {
  return (
    <div className="w-full min-w-0 flex flex-col gap-5">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="rounded-lg border border-border bg-bg-muted p-5 space-y-4 shadow-card">
          <Skeleton className="h-9 w-52" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((__, j) => (
              <Skeleton key={j} className="h-10 w-full" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Sección de config editable (genérica) ────────────────────────────────────
// `save(patch)` hace el PUT y devuelve la respuesta; `onSaved(res)` la propaga.

function ConfigSection({
  icon,
  title,
  subtitle,
  fields,
  values,
  bounds,
  defaults,
  updatedAt,
  crossValidate,
  savedMsg,
  save,
  onSaved,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => formFrom(fields, values));
  const [saving, setSaving] = useState(false);

  // Re-hidratar el form cuando llegan/cambian los valores (recarga o guardado).
  useEffect(() => {
    setForm(formFrom(fields, values));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const errors = {};
  for (const f of fields) if (f.num) errors[f.key] = numError(form[f.key], bounds?.[f.key], t);
  const crossErr = crossValidate ? crossValidate(form, t) : null;
  const hasErrors = !!crossErr || fields.some((f) => errors[f.key]);
  const dirty = fields.some((f) => form[f.key] !== fieldVal(f, values?.[f.key]));
  const hasDefaults = !!defaults && fields.some((f) => defaults[f.key] != null);

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setBool = (k) => (val) => setForm((p) => ({ ...p, [k]: val }));
  const handleReset = () => setForm(formFrom(fields, values));
  const handleRestoreDefaults = () =>
    setForm((p) => ({
      ...p,
      ...Object.fromEntries(
        fields.filter((f) => defaults?.[f.key] != null).map((f) => [f.key, fieldVal(f, defaults[f.key])]),
      ),
    }));

  const handleSave = async () => {
    if (!dirty || hasErrors) return;
    setSaving(true);
    try {
      const res = await save(buildPatch(fields, form));
      onSaved?.(res);
      showToast(t(savedMsg), true);
    } catch (err) {
      showToast(apiErrMsg(err, t, false), false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      icon={icon}
      title={title}
      subtitle={subtitle}
      action={updatedAt !== undefined ? <UpdatedAt value={updatedAt} t={t} /> : null}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {fields.map((f) => {
          const [min, max] = f.num ? bounds?.[f.key] || [] : [];
          const def = defaults?.[f.key];
          const hint = f.hint
            ? t(f.hint)
            : f.num && min != null && max != null
              ? t("Rango {{min}}–{{max}} · por defecto {{def}}", { min, max, def: def ?? "—" })
              : undefined;
          return (
            <Field
              key={f.key}
              label={f.mono ? <ParamLabel name={f.key}>{t(f.label)}</ParamLabel> : t(f.label)}
              hint={hint}
              error={errors[f.key]}
              className={f.full ? "sm:col-span-2" : ""}
            >
              {f.bool ? (
                <div className="h-10 flex items-center">
                  <Switch
                    checked={!!form[f.key]}
                    onChange={setBool(f.key)}
                    disabled={saving}
                    ariaLabel={typeof f.label === "string" ? t(f.label) : undefined}
                  />
                </div>
              ) : f.secret ? (
                <SecretInput value={form[f.key]} onChange={setField(f.key)} disabled={saving} placeholder={f.placeholder} />
              ) : (
                <Input
                  type={f.num ? "number" : "text"}
                  inputMode={f.num ? (f.float ? "decimal" : "numeric") : undefined}
                  step={f.num ? (f.float ? "any" : "1") : undefined}
                  min={min}
                  max={max}
                  value={form[f.key]}
                  onChange={setField(f.key)}
                  placeholder={f.placeholder}
                  invalid={!!errors[f.key]}
                  disabled={saving}
                  className={f.mono ? "font-mono" : undefined}
                />
              )}
            </Field>
          );
        })}
      </div>

      {crossErr && <p className="text-xs text-status-error">{crossErr}</p>}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        {hasDefaults ? (
          <Button variant="ghost" onClick={handleRestoreDefaults} disabled={saving} iconLeft={<RotateCcw size={16} />}>
            {t("Restaurar valores por defecto")}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReset} disabled={!dirty || saving}>
            {t("Cancelar")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!dirty || hasErrors || saving}
            loading={saving}
            iconLeft={<Save size={16} />}
          >
            {saving ? t("Guardando…") : t("Guardar")}
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}

// ── Editor de prompt genérico (self-loading vía load/save) ───────────────────

function PromptEditor({ icon, title, subtitle, load, save, savedMsg = "Prompt guardado" }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const doLoad = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await load();
      setData(d);
      setText(d?.text ?? "");
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    doLoad();
  }, [doLoad]);

  const len = text.length;
  const dirty = data != null && text !== (data.text ?? "");
  const tooLong = len > MAX_PROMPT;
  const empty = len < 1;

  const handleReset = () => {
    if (data) setText(data.text ?? "");
  };
  const handleSave = async () => {
    if (!dirty || empty || tooLong) return;
    setSaving(true);
    try {
      const res = await save(text);
      const next = res && typeof res === "object" && "text" in res ? res : { ...data, text };
      setData(next);
      setText(next.text ?? text);
      showToast(t(savedMsg), true);
    } catch (err) {
      showToast(apiErrMsg(err, t, false), false);
    } finally {
      setSaving(false);
    }
  };

  const hasMeta = data && (data.source || "updated_at" in data);

  return (
    <SectionCard
      icon={icon}
      title={title}
      subtitle={subtitle}
      action={
        !loading && !error && hasMeta ? (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {data.source && (
              <Badge tone={data.source === "db" ? "info" : "neutral"} dot>
                {sourceLabel(data.source, t)}
              </Badge>
            )}
            {"updated_at" in data && <UpdatedAt value={data.updated_at} t={t} />}
          </div>
        ) : null
      }
    >
      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : error ? (
        <InlineError message={apiErrMsg(error, t, true)} onRetry={doLoad} t={t} />
      ) : (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={MAX_PROMPT}
            rows={20}
            spellCheck={false}
            disabled={saving}
            placeholder={t("Prompt de la IA…")}
            className="w-full resize-y rounded-lg border border-border bg-bg px-3 py-2 text-sm text-fg font-mono leading-relaxed placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:opacity-50"
          />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className={`text-xs ${tooLong ? "text-status-error" : "text-fg-muted"}`}>
              {len.toLocaleString("es-CL")}/{MAX_PROMPT.toLocaleString("es-CL")} {t("caracteres")}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleReset} disabled={!dirty || saving}>
                {t("Cancelar")}
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={!dirty || empty || tooLong || saving}
                loading={saving}
                iconLeft={<Save size={16} />}
              >
                {saving ? t("Guardando…") : t("Guardar")}
              </Button>
            </div>
          </div>
        </>
      )}
    </SectionCard>
  );
}

// ── GENERAL — monitoreo read-only del servidor Ollama ────────────────────────

function GeneralSection() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (soft) => {
    soft ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      setData(await calidadIaConfigService.getSystem());
    } catch (err) {
      setError(err);
    } finally {
      soft ? setRefreshing(false) : setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  if (loading) return <SectionsSkeleton />;
  if (error)
    return (
      <SectionCard icon={<Server className="w-4 h-4" aria-hidden="true" />} title={t("General")}>
        <InlineError message={apiErrMsg(error, t, true)} onRetry={() => load(false)} t={t} />
      </SectionCard>
    );
  if (!data) return null;

  const ollama = data.ollama ?? {};
  const workers = data.workers ?? {};
  const queues = workers.queues ?? {};
  const loaded = ollama.loaded ?? [];
  const available = ollama.available ?? [];

  return (
    <div className="w-full min-w-0 flex flex-col gap-5">
      <SectionCard
        icon={<Server className="w-4 h-4" aria-hidden="true" />}
        title={t("Servidor Ollama")}
        subtitle={t("Estado del modelo y la GPU (solo lectura).")}
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => load(true)}
            loading={refreshing}
            iconLeft={<RefreshCw size={14} />}
          >
            {t("Actualizar")}
          </Button>
        }
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <KV label={t("Modelo")} value={data.model_name} mono />
          <KV label={t("Modelos disponibles")} value={available.length ? available.join(", ") : "—"} mono />
        </div>

        {ollama.error && <p className="text-xs text-status-error">{String(ollama.error)}</p>}

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-fg-muted mb-2">
            {t("Modelos cargados en memoria")}
          </div>
          {loaded.length ? (
            <div className="rounded-lg border border-border bg-bg overflow-hidden">
              {loaded.map((m) => (
                <div
                  key={m.name}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm border-b border-border last:border-0"
                >
                  <span className="font-mono text-fg">{m.name}</span>
                  <span className="text-fg-muted whitespace-nowrap">
                    {fmtGB(m.size_vram)} · {t("expira")} {fmtDate(m.expires_at, t)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-fg-muted">{t("No hay modelos cargados en memoria.")}</p>
          )}
        </div>
      </SectionCard>

      <SectionCard
        icon={<Cpu className="w-4 h-4" aria-hidden="true" />}
        title={t("Workers y colas")}
        subtitle={t("Procesos de análisis y trabajo pendiente.")}
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label={t("Workers vivos")} value={workers.alive ?? "—"} />
          <Stat label={t("Cola de análisis")} value={queues.analyze ?? "—"} />
          <Stat label={t("Cola backfill")} value={queues.analyze_backfill ?? "—"} />
          <Stat label={t("Cola webhooks")} value={queues.webhooks ?? "—"} />
        </div>
      </SectionCard>
    </div>
  );
}

// ── HELPDESK — system prompt + perfiles free/portal + config ollama ──────────

function HelpdeskSection() {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCfg(await calidadIaConfigService.getHelpdesk());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <SectionsSkeleton />;
  if (error)
    return (
      <SectionCard icon={<Headset className="w-4 h-4" aria-hidden="true" />} title={t("Helpdesk")}>
        <InlineError message={apiErrMsg(error, t, true)} onRetry={load} t={t} />
      </SectionCard>
    );
  if (!cfg) return null;

  return (
    <div className="w-full min-w-0 flex flex-col gap-5">
      <PromptEditor
        icon={<FileText className="w-4 h-4" aria-hidden="true" />}
        title={t("System prompt del chat")}
        subtitle={t("Instrucciones base del asistente del helpdesk.")}
        load={() => calidadIaConfigService.getHelpdesk().then((c) => ({ text: c?.system_prompt ?? "" }))}
        save={(content) => calidadIaConfigService.putHelpdeskPrompt(content)}
        savedMsg="System prompt guardado"
      />
      <ConfigSection
        icon={<SlidersHorizontal className="w-4 h-4" aria-hidden="true" />}
        title={t("Perfil free")}
        subtitle={t("Parámetros de generación del chat para usuarios sin sesión.")}
        fields={HELPDESK_MODEL_FIELDS}
        values={cfg.model?.free}
        savedMsg="Perfil free guardado"
        save={(patch) => calidadIaConfigService.putHelpdeskModel("free", patch)}
        onSaved={load}
      />
      <ConfigSection
        icon={<SlidersHorizontal className="w-4 h-4" aria-hidden="true" />}
        title={t("Perfil portal")}
        subtitle={t("Parámetros de generación del chat para usuarios en el portal.")}
        fields={HELPDESK_MODEL_FIELDS}
        values={cfg.model?.portal}
        savedMsg="Perfil portal guardado"
        save={(patch) => calidadIaConfigService.putHelpdeskModel("portal", patch)}
        onSaved={load}
      />
      <ConfigSection
        icon={<Cpu className="w-4 h-4" aria-hidden="true" />}
        title={t("Ollama (chat)")}
        subtitle={t("Keep-alive y precarga del modelo. Keep-alive es compartido con Calidad.")}
        fields={HELPDESK_OLLAMA_FIELDS}
        values={cfg.ollama}
        savedMsg="Configuración de Ollama guardada"
        save={(patch) => calidadIaConfigService.putHelpdeskOllama(patch)}
        onSaved={load}
      />
    </div>
  );
}

// ── Modelo (read-only; el servidor Ollama es compartido → se gestiona en General) ──

function ModelCard({ modelName }) {
  const { t } = useTranslation();
  return (
    <SectionCard
      icon={<Cpu className="w-4 h-4" aria-hidden="true" />}
      title={t("Modelo")}
      subtitle={t("Compartido con el chat (helpdesk). No editable: cargar otro modelo tumbaría el helpdesk por memoria de GPU.")}
      action={
        <Badge tone="neutral" dot>
          {t("Solo lectura")}
        </Badge>
      }
    >
      <Field
        label={<ParamLabel name="model_name">{t("Modelo de Ollama")}</ParamLabel>}
        hint={t("Se gestiona en General; aquí es solo lectura.")}
      >
        <Input value={modelName || ""} readOnly disabled className="font-mono" />
      </Field>
    </SectionCard>
  );
}

// ── CALIDAD — evaluación QA ──────────────────────────────────────────────────

function CalidadSection() {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setConfig(await calidadIaConfigService.getConfig());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  if (loading) return <SectionsSkeleton />;
  if (error)
    return (
      <SectionCard icon={<ShieldCheck className="w-4 h-4" aria-hidden="true" />} title={t("Calidad")}>
        <InlineError message={apiErrMsg(error, t, true)} onRetry={loadConfig} t={t} />
      </SectionCard>
    );
  if (!config) return null;

  return (
    <div className="w-full min-w-0 flex flex-col gap-5">
      <ModelCard modelName={config.model_name} />
      <ConfigSection
        icon={<SlidersHorizontal className="w-4 h-4" aria-hidden="true" />}
        title={t("Generación (Ollama)")}
        subtitle={t("Controlan cómo genera el modelo. Se validan contra los rangos permitidos.")}
        fields={OLLAMA_FIELDS}
        values={config.ollama}
        bounds={config.bounds?.ollama}
        defaults={config.defaults?.ollama}
        updatedAt={config.updated_at?.ollama}
        savedMsg="Parámetros de generación guardados"
        save={(patch) => calidadIaConfigService.putConfig({ ollama: patch })}
        onSaved={setConfig}
      />
      <ConfigSection
        icon={<MapPin className="w-4 h-4" aria-hidden="true" />}
        title={t("Umbrales geo")}
        subtitle={t("Distancias (m) para clasificar la ubicación de una entrega.")}
        fields={GEO_FIELDS}
        values={config.geo}
        bounds={config.bounds?.geo}
        defaults={config.defaults?.geo}
        updatedAt={config.updated_at?.geo}
        crossValidate={geoCrossError}
        savedMsg="Umbrales geo guardados"
        save={(patch) => calidadIaConfigService.putConfig({ geo: patch })}
        onSaved={setConfig}
      />
      <ConfigSection
        icon={<WebhookIcon className="w-4 h-4" aria-hidden="true" />}
        title={t("Webhook")}
        subtitle={t("A dónde calidad-ia entrega los veredictos de análisis.")}
        fields={WEBHOOK_FIELDS}
        values={config.webhook}
        bounds={config.bounds?.webhook}
        updatedAt={config.updated_at?.webhook}
        savedMsg="Webhook guardado"
        save={(patch) => calidadIaConfigService.putConfig({ webhook: patch })}
        onSaved={setConfig}
      />
      <PromptEditor
        icon={<FileText className="w-4 h-4" aria-hidden="true" />}
        title={t("System prompt")}
        subtitle={t("Instrucciones base que recibe la IA en cada análisis.")}
        load={() => calidadIaConfigService.getPrompt("system")}
        save={(text) => calidadIaConfigService.putPrompt("system", text)}
      />
      <PromptEditor
        icon={<FileText className="w-4 h-4" aria-hidden="true" />}
        title={t("Prompt de respaldo")}
        subtitle={t("Versión simplificada, usada como fallback cuando el principal falla.")}
        load={() => calidadIaConfigService.getPrompt("simplified")}
        save={(text) => calidadIaConfigService.putPrompt("simplified", text)}
      />
    </div>
  );
}

// ── Panel ────────────────────────────────────────────────────────────────────

export default function IaConfigView() {
  const { t } = useTranslation();
  // El tab vive en la URL para que /admin/ai-health pueda mandar a ?tab=salud y
  // para que un enlace a una sección concreta sobreviva al reload.
  const [section, setSection] = useUrlState("tab", "salud", { push: true });

  return (
    <div className="w-full min-w-0 p-4 sm:p-6 space-y-4 delivery-quality-view">
      <PageHeader
        icon={<Bot className="w-6 h-6 text-fg-muted" />}
        title={t("Inteligencia artificial")}
        subtitle={t(
          "Salud del pipeline y configuración de la IA: la compartida (General), la del chat (Helpdesk) y la de la evaluación de calidad (Calidad). Los workers leen los cambios en vivo (~15 s), sin reinicio.",
        )}
      />

      <Tabs value={section} onValueChange={setSection}>
        <TabsList ariaLabel={t("Secciones de IA")}>
          <TabsTrigger value="salud" icon={<HeartPulse className="w-4 h-4" />}>
            {t("Salud")}
          </TabsTrigger>
          <TabsTrigger value="general" icon={<Server className="w-4 h-4" />}>
            {t("General")}
          </TabsTrigger>
          <TabsTrigger value="helpdesk" icon={<Headset className="w-4 h-4" />}>
            {t("Helpdesk")}
          </TabsTrigger>
          <TabsTrigger value="calidad" icon={<ShieldCheck className="w-4 h-4" />}>
            {t("Calidad")}
          </TabsTrigger>
        </TabsList>

        {/* El monitor del pipeline se monta solo cuando su tab está activo: trae
            su propio auto-refresh y no queremos que corra de fondo. */}
        <TabsContent value="salud" className="mt-4">
          {section === "salud" && <AiPerformanceTab />}
        </TabsContent>
        <TabsContent value="general" className="mt-4">
          <GeneralSection />
        </TabsContent>
        <TabsContent value="helpdesk" className="mt-4">
          <HelpdeskSection />
        </TabsContent>
        <TabsContent value="calidad" className="mt-4">
          <CalidadSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
